import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Dialog } from '@reach/dialog'
import { AsyncView } from '@aboutbits/react-toolbox'
import { DialogSearchHeader } from '../../../../shared/dialog/DialogSearchHeader'
import { useSearchAndPaginationInMemory } from '../../../../shared/pagination/inMemoryPagination'
import {
  getProjectById,
  useGetFavoriteProjects,
  useGetProjects,
} from '../../../project/db/projects'
import { DialogGenericAction } from '../../../../shared/dialog/DialogGenericAction'
import { IconDelete, IconQrCodeScanner } from '../../../../shared/svgs'
import { Project, ProjectVisibility } from '../../../project/types'
import { translationsProject } from '../../../project/translations/translationsProject'
import { translationsShared } from '../../../../shared/translations/translationsShared'
import { QrCodeView } from '../activityForm/steps/QrCodeView'
import { ContentArea } from '../../../../shared/content'
import { ProjectDialogList } from './ProjectDialogList'
import { ProjectDialogFavorites } from './ProjectDialogFavorites'

const ProjectDialog: React.FC<{
  onDismiss: (event?: React.SyntheticEvent<Element, Event> | undefined) => void
  isOpen: boolean
  onConfirm: (projectId: string, project: Project) => void
  onClear: () => void
  filter?: (project: Project) => boolean
}> = ({ onDismiss, onConfirm, isOpen, onClear, filter }) => {
  const intl = useIntl()
  const { page, size, search, searchActions, paginationActions } =
    useSearchAndPaginationInMemory()
  const [scanning, setScanning] = useState<boolean>(false)

  const searching = search !== ''

  const { data } = useGetProjects(
    search,
    page,
    size,
    ProjectVisibility.projects,
    filter
  )
  const { data: projectFavorites } = useGetFavoriteProjects(
    ProjectVisibility.projects,
    filter
  )

  const title = searching
    ? intl.formatMessage({
        id: 'activity.project.overview.search.result',
        defaultMessage: 'Search result',
      })
    : intl.formatMessage({
        id: 'activity.project.overview.list.all',
        defaultMessage: 'All projects',
      })

  const empty = searching
    ? intl.formatMessage({
        id: 'activity.project.overview.search.empty',
        defaultMessage: 'No projects could be found',
      })
    : intl.formatMessage({
        id: 'activity.project.overview.list.empty',
        defaultMessage: 'No projects available',
      })

  return (
    <Dialog
      isOpen={isOpen}
      aria-label={intl.formatMessage(translationsProject.overviewTitle)}
      className="mx-auto bg-white md:my-20 md:w-full md:max-w-lg md:bg-transparent"
    >
      {!scanning ? (
        <>
          <DialogSearchHeader
            onDismiss={onDismiss}
            title={title}
            searchLabel={intl.formatMessage(
              translationsProject.overviewSearchStart
            )}
            search={search}
            searchActions={searchActions}
            additionalAction={
              <>
                <DialogGenericAction
                  icon={IconDelete}
                  label={intl.formatMessage({
                    id: 'activity.form.remove.project',
                    defaultMessage: 'Remove linked project',
                  })}
                  onClick={onClear}
                />
                <DialogGenericAction
                  icon={IconQrCodeScanner}
                  label={intl.formatMessage(translationsShared.scanQRCodeTitle)}
                  onClick={() => {
                    setScanning(!scanning)
                  }}
                />
              </>
            }
          />
          <ContentArea>
            {page === 1 && search === '' && (
              <AsyncView
                data={projectFavorites}
                renderSuccess={(projects) =>
                  projects && (
                    <ProjectDialogFavorites
                      data={projects}
                      onSelect={onConfirm}
                    />
                  )
                }
              />
            )}
            <AsyncView
              data={data}
              renderSuccess={(data) =>
                data && (
                  <ProjectDialogList
                    title={title}
                    empty={empty}
                    data={data}
                    onSelect={onConfirm}
                    paginationActions={paginationActions}
                  />
                )
              }
            />
          </ContentArea>
        </>
      ) : (
        <QrCodeView
          onDismiss={() => setScanning(false)}
          onError={() => {
            setScanning(false)
          }}
          onScan={async (result) => {
            if (result) {
              try {
                const item = await getProjectById(
                  result,
                  ProjectVisibility.projects,
                  filter
                )

                if (item) {
                  onConfirm(item.id, item)
                } else {
                  setScanning(false)
                }
              } catch (e) {
                console.error(e)
                setScanning(false)
              }
            }
          }}
          title={
            <FormattedMessage {...translationsProject.overviewSearchStart} />
          }
        />
      )}
    </Dialog>
  )
}

export { ProjectDialog }
